import Config from 'config';
import { useUnit } from 'effector-react';
import { $queryParams } from 'models/location';
import { navigateFx } from 'models/router-gate';
import { $global, storage } from 'models/store-global';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import 'react-phone-input-2/lib/bootstrap.css';

import './CustomSignIn.scss';

import PasswordFormControl from 'components/FormControl/Password/PasswordFormControl';
import { Button, Link } from 'components/StyledTemplates/StyledTemplates';
import MfaPage from 'components/pages/MfaPage/MfaPage';
import ResetPassword from 'components/pages/ResetPassword/ResetPassword';

import AuthService from 'services/AuthService';

import useResources from 'hooks/useResources';

import AdvisorLogo from 'assets/OriginLogo.png';
import Logo from 'assets/OriginLogo.png';
import signin_page_image_advisor from 'assets/signin_page_image_advisor.png';
import signin_page_image_advisor_webp from 'assets/signin_page_image_advisor.webp';
import signin_page_image from 'assets/signin_page_image_big.png';
import signin_page_image_webp from 'assets/signin_page_image_big.webp';

import { MyStore } from 'types/Store';

const CustomSignIn = () => {
    const { t } = useResources();

    const { role } = useUnit($queryParams);

    // const [isLoading, setLoading] = useState(true);
    const [user, setUser] = useState({ username: '', password: '' });
    const [shouldResetPassword, setShouldResetPassword] = useState(false);
    const [shouldDoMfa, setShouldDoMfa] = useState(false);
    const [signInResult, setSignInResult] = useState({} as any);
    const [isSubmitting, setSubmitting] = useState(false);
    // const loginRole = useUnit($global.map((state) => state.loginRole));
    const loginRole = 'user';
    const validated = useUnit($global.map((state) => state?.validated || false));
    const setValidated = (newValue: boolean) => {
        storage.set((store): MyStore => ({ ...store, authData: { ...(store.authData || {}), validated: newValue } }));
    };

    // const isAdvisorSignInPage = role === 'advisor' || loginRole === 'advisor';
    const isAdvisorSignInPage = false;

    const handlers = {
        handleChange: (event: ChangeEvent<HTMLInputElement>) => {
            const value =
                event.target.type === 'email'
                    ? (event?.target?.value ?? '')?.trim?.()?.toLowerCase?.()
                    : event?.target?.value;
            const updatedUser = {
                ...user,
                [event.target.id]: value || '',
            };
            setUser(updatedUser);
        },
        handleSubmit: async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            event.stopPropagation();
            const form = event.currentTarget;
            setValidated(true);
            if (!form.checkValidity()) {
                return;
            }
            setSubmitting(true);
            const result = await AuthService.complexSignIn(user);
            setValidated(false);
            if (result && result.challengeName === 'NEW_PASSWORD_REQUIRED') {
                setShouldResetPassword(true);
                setSignInResult(result);
            } else if (result && result.challengeName === 'CUSTOM_CHALLENGE') {
                setShouldDoMfa(true);
                setSignInResult(result);
            } else if (result !== undefined) {
                navigateFx(isAdvisorSignInPage ? '/advisor/dashboard' : '/dashboard');
            }
            setSubmitting(false);
        },
        authWithGoogle: async () => {
            await AuthService.signInWith('Google');
        },
        authWithFacebook: async () => {
            await AuthService.signInWith('Facebook');
        },
    };

    const exitMfaPage = () => {
        setShouldDoMfa(false);
    };

    useEffect(() => {
        setValidated(false);
    }, []);

    useEffect(() => {
        if (loginRole) {
            navigateFx(`?role=${loginRole}`);
            storage.set(({ loginRole, ...store }): MyStore => store);
        }
    }, [loginRole]);

    useEffect(() => {
        return () => {
            storage.set(({ loginRole, ...store }): MyStore => store);
        };
    }, []);

    useEffect(() => {
        AuthService.cleanCognitoCookies();
    }, []);

    useEffect(() => {
        if (role === 'advisor') {
            storage.set((store): MyStore => ({ ...store, loginRole: 'advisor' }));
        }
    }, [role]);

    if (shouldResetPassword) {
        return <ResetPassword user={signInResult} />;
    }

    if (shouldDoMfa) {
        return <MfaPage user={signInResult} onAttemptsLimitReached={exitMfaPage} onBackToSignIn={exitMfaPage} />;
    }

    return (
        <div className="row signup-row custom-sign-in">
            <div className="col-sm text-center form-holder font-weight-bold text-dark">
                <div>
                    <h1 className="mb-4 d-flex justify-content-center">
                        <img src={isAdvisorSignInPage ? AdvisorLogo : Logo} alt="logo" height="50px" />
                    </h1>
                    <h5 className="mb-4">{t('signIn.sign_in_to_your_acc')}</h5>
                    <Form
                        className="sigininform my-4"
                        noValidate
                        validated={validated}
                        onSubmit={handlers.handleSubmit}
                    >
                        <Form.Group controlId="username">
                            <Form.Control
                                required
                                value={user.username}
                                type="email"
                                placeholder={t('signIn.email')}
                                onChange={handlers.handleChange}
                                data-hj-allow
                            />
                            <Form.Control.Feedback type="invalid">{t('signIn.email_error')}</Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group controlId="password">
                            <PasswordFormControl
                                required
                                placeholder={t('signIn.password')}
                                onChange={handlers.handleChange}
                            />
                            <Form.Control.Feedback type="invalid">{t('signIn.password_error')}</Form.Control.Feedback>
                        </Form.Group>
                        <Button
                            data-qaid="login_button"
                            variant="origin_green"
                            type="submit"
                            block
                            size="lg"
                            className="mb-2"
                            disabled={isSubmitting}
                        >
                            {t('signIn.login')}
                        </Button>
                        {Config.canLoginWithSocialMedia && (
                            <div className="d-flex flex-row justify-content-around">
                                <Button
                                    data-qaid="google_button"
                                    variant="primary"
                                    size="sm"
                                    className="mb-2"
                                    onClick={handlers.authWithGoogle}
                                >
                                    {t('signIn.google')}
                                </Button>
                                <Button
                                    data-qaid="facebook_button"
                                    variant="primary"
                                    size="sm"
                                    className="mb-2"
                                    onClick={handlers.authWithFacebook}
                                >
                                    {t('signIn.facebook')}
                                </Button>
                            </div>
                        )}

                        <Link to={isAdvisorSignInPage ? `/forgot-password?role=advisor` : '/forgot-password'}>
                            <Button data-qaid="forgot_password_button" variant="bodybg" block>
                                {t('signIn.forgot_password')}
                            </Button>
                        </Link>
                    </Form>
                </div>
            </div>
            <div className="col-sm image-holder d-none d-sm-flex">
                <picture>
                    <source
                        srcSet={isAdvisorSignInPage ? signin_page_image_advisor_webp : signin_page_image_webp}
                        type="image/webp"
                    />
                    <img
                        src={isAdvisorSignInPage ? signin_page_image_advisor : signin_page_image}
                        className="img-fluid"
                        alt="sign-in"
                    />
                </picture>
            </div>
        </div>
    );
};

export default CustomSignIn;
